import { useEffect } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"
import { usePageTracking } from "../../../../../hooks"
import { AnalyticsPageCategory, AnalyticsPageType } from "../../../../../models"

import {
  createTheme,
  fetchAllLicences,
  fetchChapterListByLicenceIds,
  fetchRegionListByChapterAndLicenceIds,
  fetchTheme,
  loadDefaultTheme,
  updateTheme
} from "../../../../../redux"
import type { AppState, AppThunkDispatch, Chapter, Licence, Region, Theme, ThemeActions } from "../../../../../redux"
import { BackButton, PageContainer } from "../../../../molecules"
import { ThemeManagerForm } from "../../../../organisms"

export interface IThemeManagerPageProps {
  theme: Theme
  chapters: Chapter[]
  regions: Region[]
  licences: Licence[]
  fetchTheme: (id: string) => void
  fetchDefaultTheme: () => void
  createTheme: (theme: Theme) => void
  updateTheme: (theme: Theme) => void
  fetchChapters: (licenceIds: string[]) => void
  fetchRegions: (chapterId: string, licenceIds: string[]) => void
  fetchLicences: () => void
}

export interface IThemeManagerRouteParams {
  themeId?: string
}

function ThemeManagerPageView(props: IThemeManagerPageProps & IThemeManagerRouteParams): ReactElement {
  const {
    themeId,
    theme,
    chapters,
    regions,
    licences,
    fetchTheme,
    fetchDefaultTheme,
    createTheme,
    updateTheme,
    fetchChapters,
    fetchRegions,
    fetchLicences
  } = props

  usePageTracking(AnalyticsPageCategory.ContentManager, AnalyticsPageType.Theme, themeId)
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchLicences()
    if (themeId && themeId !== theme.id) {
      fetchTheme(themeId)
    } else if (!themeId) {
      fetchDefaultTheme()
    }
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (theme.licenceIds && theme.licenceIds.length > 0 && chapters.length === 0) {
      fetchChapters(theme.licenceIds)
    }
    if (theme.chapter && chapters && regions.length === 0 && theme.licenceIds.length > 0) {
      fetchRegions(theme.chapter.id as string, theme.licenceIds)
    }
  }, [theme])

  function hasLoaded(): boolean {
    if ((themeId === theme.id || !themeId) && chapters && theme) {
      return true
    }
    return false
  }

  function handleSubThemeSave(theme: Theme): void {
    if (theme.id) {
      updateTheme(theme)
    } else {
      createTheme(theme)
    }
  }

  function handleLicencesChanged(licenceIds: string[]) {
    fetchChapters(licenceIds)
  }

  return (
    <PageContainer hasLoaded={hasLoaded()} id="subtheme-manager-page" smallPadding>
      <BackButton />

      <ThemeManagerForm
        theme={theme}
        chapters={chapters}
        regions={regions}
        licences={licences}
        saveTheme={handleSubThemeSave}
        onChapterChange={fetchRegions}
        onLicenceIdsChanged={handleLicencesChanged}
      />
    </PageContainer>
  )
}

function mapStateToProps(state: AppState, ownProps: IThemeManagerRouteParams) {
  return {
    themeId: ownProps.themeId,
    theme: state.theme.theme,
    chapters: state.chapters.chapters ? state.chapters.chapters : [],
    regions: state.region.regions,
    licences: state.licences.licences
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<ThemeActions>) {
  return {
    fetchTheme: (id: string) => dispatch(fetchTheme(id)),
    fetchDefaultTheme: () => dispatch(loadDefaultTheme()),
    createTheme: (theme: Theme) => dispatch(createTheme(theme)),
    updateTheme: (theme: Theme) => dispatch(updateTheme(theme)),
    fetchChapters: (licenceIds: string[]) => dispatch(fetchChapterListByLicenceIds(licenceIds)),
    fetchRegions: (chapterId: string, licenceIds: string[]) =>
      dispatch(fetchRegionListByChapterAndLicenceIds(chapterId, licenceIds)),
    fetchLicences: () => dispatch(fetchAllLicences())
  }
}

export const ThemeManagerPage = connect(mapStateToProps, mapDispatchToProps)(ThemeManagerPageView)
